import React from 'react';
import BatteryStdIcon from '@material-ui/icons/BatteryStd';
import SyncProblemIcon from '@material-ui/icons/SyncProblem';
import SignalCellularAltIcon from '@material-ui/icons/SignalCellularAlt';
import NoTargetIcon from '@material-ui/icons/DeviceHub';
import SyncIcon from '@material-ui/icons/Sync';
import { KnownIssues } from './types';
import { chartColor } from '../Dashboard/provider';

export const categories = {
  OK: {
    name: 'OK',
    value: 'ok',
  },
  WARNING: {
    name: 'Warning',
    value: 'warning',
  },
  CRITICAL: {
    name: 'Critical',
    value: 'critical',
  },
};

const now = new Date().toISOString();

export const initialKnownIssues: KnownIssues = {
  isBehindSyncingSchedule: {
    label: 'Behind on syncing schedule',
    deviceCount: 0,
    severity: categories.CRITICAL.value,
    link: `/devices?to_next_communication_at=${now}`,
    queryValue: encodeURIComponent(now),
    barChartColor: '#2C0542', //deep purple
    icon: (size = {}, position = {}) => (
      <SyncProblemIcon
        color="error"
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
        style={{ fontSize: size.fontSize }}
      />
    ),
  },
  hasLowBattery: {
    label: 'Battery voltage low',
    deviceCount: 0,
    severity: categories.WARNING.value,
    link: `/devices?battery_voltage_health=${categories.WARNING.value}`,
    barChartColor: '#F3D23E', //yellow
    icon: (size = {}, position = {}) => (
      <BatteryStdIcon
        style={{
          fontSize: size.fontSize,
          color: chartColor(categories.WARNING.value),
        }}
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
      />
    ),
  },

  hasMinBattery: {
    label: 'Battery voltage critical',
    deviceCount: 0,
    severity: categories.CRITICAL.value,
    link: `/devices?battery_voltage_health=${categories.CRITICAL.value}`,
    barChartColor: '#6E1031', //light purple
    icon: (size = {}, position = {}) => (
      <BatteryStdIcon
        color="error"
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
        style={{ fontSize: size.fontSize }}
      />
    ),
  },
  hasAvgLowBattery: {
    label: 'Average battery voltage low',
    deviceCount: 0,
    severity: categories.WARNING.value,
    link: `/devices?battery_voltage_average_health=${categories.WARNING.value}`,
    barChartColor: '#F3D23E', //yellow
    icon: (size = {}, position = {}) => (
      <BatteryStdIcon
        style={{
          fontSize: size.fontSize,
          color: chartColor(categories.WARNING.value),
        }}
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
      />
    ),
  },

  hasAvgMinBattery: {
    label: 'Average battery voltage critical',
    deviceCount: 0,
    severity: categories.CRITICAL.value,
    link: `/devices?battery_voltage_average_health=${categories.CRITICAL.value}`,
    barChartColor: '#6E1031', //light purple
    icon: (size = {}, position = {}) => (
      <BatteryStdIcon
        color="error"
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
        style={{ fontSize: size.fontSize }}
      />
    ),
  },
  hasLowSignalStrength: {
    label: 'Signal strength low',
    deviceCount: 0,
    severity: categories.WARNING.value,
    link: `/devices?signal_strength_health=${categories.WARNING.value}`,
    barChartColor: '#F9A571', //orange
    icon: (size = {}, position = {}) => (
      <SignalCellularAltIcon
        style={{
          fontSize: size.fontSize,
          color: chartColor(categories.WARNING.value),
        }}
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
      />
    ),
  },
  hasMinSignalStrength: {
    label: 'Signal strength critical',
    deviceCount: 0,
    severity: categories.CRITICAL.value,
    link: `/devices?signal_strength_health=${categories.CRITICAL.value}`,
    barChartColor: '#FF5432', //red
    icon: (size = {}, position = {}) => (
      <SignalCellularAltIcon
        color="error"
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
        style={{ fontSize: size.fontSize }}
      />
    ),
  },
  hasAvgLowSignalStrength: {
    label: 'Average signal strength low',
    deviceCount: 0,
    severity: categories.WARNING.value,
    link: `/devices?signal_strength_average_health=${categories.WARNING.value}`,
    barChartColor: '#F9A571', //orange
    icon: (size = {}, position = {}) => (
      <SignalCellularAltIcon
        style={{
          fontSize: size.fontSize,
          color: chartColor(categories.WARNING.value),
        }}
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
      />
    ),
  },
  hasAvgMinSignalStrength: {
    label: 'Average signal strength critical',
    deviceCount: 0,
    severity: categories.CRITICAL.value,
    link: `/devices?signal_strength_average_health=${categories.CRITICAL.value}`,
    barChartColor: '#FF5432', //red
    icon: (size = {}, position = {}) => (
      <SignalCellularAltIcon
        color="error"
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
        style={{ fontSize: size.fontSize }}
      />
    ),
  },
  hasNoTargetSystem: {
    label: 'No webhooks set',
    deviceCount: 0,
    severity: categories.CRITICAL.value,
    link: `/devices?noTarget=true`,
    barChartColor: '#518AF5', //blue
    icon: (size = {}, position = {}) => (
      <NoTargetIcon
        color="error"
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
        style={{ fontSize: size.fontSize }}
      />
    ),
  },
  hasPendingChanges: {
    label: 'Has pending changes',
    deviceCount: 0,
    severity: categories.WARNING.value,
    link: `/devices?pendingChange=true`,
    barChartColor: '#B9D1FF', //light blue
    icon: (size = {}, position = {}) => (
      <SyncIcon
        color="inherit"
        x={position.x}
        y={position.y}
        width={size.width}
        height={size.height}
        style={{ fontSize: size.fontSize }}
      />
    ),
  },
};
